import { useState, useEffect } from 'react'
import { Eye, EyeOff, CheckCircle, AlertCircle, Moon, Sun } from 'lucide-react'
import { supabase } from '../services/supabase'

export function ResetPassword() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [sessionChecked, setSessionChecked] = useState(false)
  const [hasValidSession, setHasValidSession] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('sfp_dark_mode') === 'true'
  })

  const toggleDarkMode = () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)
    localStorage.setItem('sfp_dark_mode', newMode ? 'true' : 'false')
  }

  useEffect(() => {
    let resolved = false

    // Listen for Supabase auth events — this is the reliable way to detect recovery/invite sessions
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[ResetPassword] Auth event:', event, 'Session:', !!session)

      if (resolved) return

      // Both PASSWORD_RECOVERY and SIGNED_IN indicate a valid reset/invite link was used
      if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'USER_UPDATED') && session) {
        resolved = true
        setHasValidSession(true)
        setSessionChecked(true)
        setUserEmail(session.user?.email || '')
        console.log('[ResetPassword] Valid session detected')
        // Clean the URL hash
        window.history.replaceState(null, '', window.location.pathname)
      }
    })

    // Immediately check for session (in case Supabase already processed the token)
    const checkImmediately = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      console.log('[ResetPassword] Immediate check - session exists:', !!session)
      if (session && !resolved) {
        resolved = true
        setHasValidSession(true)
        setSessionChecked(true)
        setUserEmail(session.user?.email || '')
        window.history.replaceState(null, '', window.location.pathname)
        return
      }
    }

    checkImmediately()

    // Fallback: if no auth event fires within 2 seconds, check again
    const timeout = setTimeout(async () => {
      if (resolved) return
      console.log('[ResetPassword] Fallback timeout: checking existing session')
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        resolved = true
        setHasValidSession(true)
      } else {
        setError('No valid session found. Please use the password reset link from your email.')
      }
      setSessionChecked(true)
    }, 2000)

    return () => {
      subscription?.unsubscribe()
      clearTimeout(timeout)
    }
  }, [])

  const validatePassword = () => {
    if (!password || !confirmPassword) {
      setError('Please fill in all password fields')
      return false
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long')
      return false
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validatePassword()) return

    setError('')
    setLoading(true)
    try {
      console.log('[ResetPassword] Updating password...')
      const { data, error: updateError } = await supabase.auth.updateUser({ password })

      if (updateError) {
        console.error('[ResetPassword] updateUser error:', updateError)
        throw updateError
      }

      console.log('[ResetPassword] Password updated successfully for:', data?.user?.email)
      setSuccess(true)

      // Sign in immediately with the new password so user doesn't have to log in again
      const email = userEmail || data?.user?.email
      if (email) {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
        if (!signInError) {
          console.log('[ResetPassword] Auto sign-in successful, redirecting to app')
          window.location.href = '/'
          return
        }
        console.warn('[ResetPassword] Auto sign-in failed, falling back to login page:', signInError.message)
      }

      // Fallback: redirect to login after short delay
      setTimeout(() => {
        window.location.href = '/'
      }, 1500)
    } catch (err) {
      console.error('[ResetPassword] Password update error:', err)
      setError(err.message || 'Failed to update password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!sessionChecked) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sfp-orange mx-auto"></div>
          <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Verifying reset link...</p>
        </div>
      </div>
    )
  }

  if (!hasValidSession) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
      }`}>
        <div className="w-full max-w-sm mx-4">
          <div className={`rounded-2xl shadow-sm p-8 text-center transition-colors duration-300 ${
            isDarkMode ? 'bg-slate-900' : 'bg-white'
          }`}>
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              isDarkMode ? 'bg-red-950' : 'bg-red-50'
            }`}>
              <AlertCircle className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
            </div>
            <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Invalid or Expired Link</h2>
            <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {error || 'This password reset link is invalid or has expired. Please request a new one.'}
            </p>
            <a
              href="/"
              className="inline-block w-full py-3 rounded-xl font-semibold text-sm text-white transition-all"
              style={{ backgroundColor: '#F5A623' }}
            >
              Back to Login
            </a>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
      }`}>
        <div className="w-full max-w-sm mx-4">
          <div className={`rounded-2xl shadow-sm p-8 text-center transition-colors duration-300 ${
            isDarkMode ? 'bg-slate-900' : 'bg-white'
          }`}>
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              isDarkMode ? 'bg-green-950' : 'bg-green-50'
            }`}>
              <CheckCircle className={`w-6 h-6 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
            </div>
            <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Password Updated!</h2>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Your password has been set. Signing you in...
            </p>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sfp-orange mx-auto"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen flex relative transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
    }`}>

      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-6 right-6 z-50 p-2.5 rounded-full transition-all duration-200"
        style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(245,166,35,0.1)',
          color: isDarkMode ? '#fff' : '#F5A623',
        }}
        title={isDarkMode ? 'Light mode' : 'Dark mode'}
      >
        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      {/* LEFT: Brand panel */}
      <div
        className="hidden lg:flex lg:w-[55%] flex-col items-center justify-center relative overflow-hidden p-16 transition-all duration-300"
        style={{
          background: isDarkMode
            ? 'linear-gradient(150deg, #1e293b 0%, #0f172a 55%, #020617 100%)'
            : 'linear-gradient(150deg, #ffffff 0%, #fdf8f0 55%, #fef9f2 100%)'
        }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(245,166,35,0.15) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Top-left ambient glow */}
        <div className="absolute pointer-events-none" style={{
          top: '-20%', left: '-20%',
          width: '70%', height: '70%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.10) 0%, transparent 65%)',
        }} />

        {/* Content */}
        <div className="relative z-10 w-full max-w-lg">
          <img
            src={isDarkMode ? '/SFP Wealth Logo - For Digital.png' : '/dark logo SFP.JPG'}
            alt="SFP Wealth"
            className="mb-14"
            style={{ width: '200px' }}
          />
          <h1 style={{
            fontSize: '2.25rem',
            fontWeight: 700,
            color: isDarkMode ? '#ffffff' : '#333333',
            lineHeight: '1.15',
            letterSpacing: '-0.03em',
            marginBottom: '1rem',
          }}>
            Set Your<br />
            <span style={{ color: '#F5A623' }}>New Password</span>
          </h1>
          <p style={{
            color: isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(51,51,51,0.50)',
            fontSize: '1rem',
            lineHeight: '1.7',
            maxWidth: '380px',
          }}>
            Create a secure password to access your SFP Wealth CRM account. Use at least 8 characters for maximum security.
          </p>
        </div>

        <div className="absolute bottom-8 left-16 flex items-center gap-2" style={{
          color: isDarkMode ? 'rgba(245,166,35,0.40)' : 'rgba(245,166,35,0.55)',
          fontSize: '0.7rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}>
          <div className="rounded-full" style={{ width: 6, height: 6, backgroundColor: '#F5A623', opacity: isDarkMode ? 0.4 : 0.6 }} />
          SFP Wealth CRM
        </div>
      </div>

      {/* RIGHT: Form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 transition-colors duration-300" style={{ backgroundColor: isDarkMode ? '#0f172a' : '#F5F5F5' }}>
        <div className="lg:hidden mb-10 text-center">
          <img src={isDarkMode ? '/SFP Wealth Logo - For Digital.png' : '/dark logo SFP.JPG'} alt="SFP Wealth" className="h-12 w-auto mx-auto" />
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 style={{
              fontSize: '1.625rem',
              fontWeight: 700,
              color: isDarkMode ? '#ffffff' : '#333333',
              letterSpacing: '-0.02em',
              marginBottom: '0.375rem',
            }}>
              Create new password
            </h2>
            <p style={{ color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(51,51,51,0.45)', fontSize: '0.875rem' }}>
              Enter a strong password for your account
            </p>
          </div>

          {error && (
            <div className="rounded-xl px-4 py-3 mb-5 text-sm" style={{
              backgroundColor: isDarkMode ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)',
              border: isDarkMode ? '1px solid rgba(239,68,68,0.40)' : '1px solid rgba(239,68,68,0.20)',
              color: isDarkMode ? '#ff6b6b' : '#dc2626',
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}>
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  className="w-full rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none transition-colors duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: loading ? (isDarkMode ? '#0f172a' : '#f5f5f5') : (isDarkMode ? '#1e293b' : '#ffffff'),
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #CCCCCC',
                    color: isDarkMode ? '#ffffff' : '#333333',
                  }}
                  onFocus={(e) => { if (!loading) e.target.style.borderColor = 'rgba(245,166,35,0.70)' }}
                  onBlur={(e) => { e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#CCCCCC' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors disabled:opacity-50"
                  style={{ color: isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}>
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  className="w-full rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none transition-colors duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: loading ? (isDarkMode ? '#0f172a' : '#f5f5f5') : (isDarkMode ? '#1e293b' : '#ffffff'),
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #CCCCCC',
                    color: isDarkMode ? '#ffffff' : '#333333',
                  }}
                  onFocus={(e) => { if (!loading) e.target.style.borderColor = 'rgba(245,166,35,0.70)' }}
                  onBlur={(e) => { e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#CCCCCC' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors disabled:opacity-50"
                  style={{ color: isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-xs text-gray-400 mb-3">
                Password must be at least 8 characters long and contain a mix of characters.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#F5A623', color: '#ffffff' }}
              onMouseEnter={(e) => { if (!loading && password && confirmPassword) e.currentTarget.style.backgroundColor = '#d4891a' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F5A623' }}
            >
              {loading ? 'Updating password...' : 'Set New Password'}
            </button>
          </form>

          <a
            href="/"
            className="block w-full text-center text-sm mt-4 transition-colors"
            style={{ color: isDarkMode ? 'rgba(255,255,255,0.40)' : 'rgba(51,51,51,0.40)' }}
            onMouseEnter={(e) => { e.target.style.color = isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}
            onMouseLeave={(e) => { e.target.style.color = isDarkMode ? 'rgba(255,255,255,0.40)' : 'rgba(51,51,51,0.40)' }}
          >
            Back to sign in
          </a>
        </div>

        <p className="absolute bottom-6 text-center text-xs" style={{ color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(51,51,51,0.25)' }}>
          &copy; {new Date().getFullYear()} SFP Wealth. All rights reserved.
        </p>
      </div>
    </div>
  )
}
